import { motion } from 'framer-motion';
import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Paper,
    Button,
    useTheme,
    alpha
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Call as CallIcon,
    Mail as MailIcon,
    Map as MapIcon
} from '@mui/icons-material';

const ContactDetail = () => {
    const theme = useTheme();

    const contactCards = [
        {
            icon: <PhoneIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: 'Phone',
            description: 'Start working with esPay that can provide everything',
            detail: '+977-1-5165236',
            link: 'tel:+977-1-5165236',
            actionIcon: <CallIcon sx={{ fontSize: 16 }} />
        },
        {
            icon: <EmailIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: 'Email',
            description: 'Start working with esPay that provide everything',
            detail: 'easysoftwarenepal@gmail.com',
            link: 'mailto:easysoftwarenepal@gmail.com',
            actionIcon: <MailIcon sx={{ fontSize: 16 }} />
        },
        {
            icon: <LocationIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: 'Location',
            description: 'Bafal - 13, Kathmandu',
            detail: 'View on Google Maps',
            link: 'https://www.google.com/maps/place/Easy+Software+Pvt.+Ltd./@27.6969556,85.2953271,17z/',
            actionIcon: <MapIcon sx={{ fontSize: 16 }} />
        }
    ];

    // Motion variants
    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: i => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.2, duration: 0.6 }
        })
    };

    return (
        <Box sx={{
            py: { xs: 8, md: 10 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
            position: 'relative'
        }}>
            {/* Decorative line */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                opacity: 0.3
            }} />

            <Container maxWidth="lg">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
                    <Typography variant="h3" component="h2" align="center" sx={{
                        fontWeight: 'bold',
                        mb: 6,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '2rem', md: '2.5rem' }
                    }}>
                        Get in Touch
                    </Typography>
                </motion.div>

                <Grid container spacing={3}>
                    {contactCards.map((card, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <motion.div custom={index} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                                <Paper sx={{
                                    height: '100%',
                                    p: 4,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    background: 'white',
                                    transition: 'all 0.3s ease-in-out',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                        borderColor: alpha(theme.palette.primary.main, 0.3)
                                    }
                                }}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        mb: 3
                                    }}>
                                        {card.icon}
                                    </Box>

                                    <Typography variant="h5" component="h3" gutterBottom sx={{
                                        fontWeight: 'bold',
                                        color: theme.palette.primary.main,
                                        mb: 2
                                    }}>
                                        {card.title}
                                    </Typography>

                                    <Typography variant="body2" sx={{
                                        color: 'text.secondary',
                                        mb: 3,
                                        lineHeight: 1.6
                                    }}>
                                        {card.description}
                                    </Typography>

                                    <Button href={card.link} target={card.title === 'Location' ? '_blank' : '_self'} startIcon={card.actionIcon} sx={{
                                        color: theme.palette.primary.main,
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}>
                                        {card.detail}
                                    </Button>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.6 }}>
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3, fontWeight: 'medium' }}>
                            We're always ready to help you with any questions
                        </Typography>
                        <Button variant="contained" size="large" sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            fontWeight: 'bold',
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            transition: 'all 0.3s ease-in-out'
                        }}>
                            Contact Us Now
                        </Button>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default ContactDetail;
